import React, { useState, useMemo } from 'react'
import { User, CheckCircle2, XCircle, Plus, X, MessageSquare, ShieldOff, MessageCircle, AlertCircle, Settings, Sliders } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card'
import { Badge } from './ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'
import { Switch } from './ui/switch'
import { Input } from './ui/input'
import { Button } from './ui/button'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion'
import { PersonDetailPanel } from './PersonDetailPanel'
import { OrganizationView } from './OrganizationView'
import { ConnectionsGraph } from './ConnectionsGraph'
import { Person, OrgNode, ConnectedSource, AssistantBehavior, AssistantConnection, AccessGuardrails } from '../types/profile'
import { GraphNode, GraphEdge } from '../types/connections'
import { useToast } from './ui/toast'
import { useConversations } from '../context/ConversationsContext'
import { useReviews } from '../context/ReviewsContext'
import { cn } from '../lib/utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog'
import { Slider } from './ui/slider'
import { Label } from './ui/label'

interface ProfileProps {
  isOpen: boolean
  onClose: () => void
  onTalkToAssistant: (person: Person) => void
  currentUserName?: string
}

// Mock data
const mockConnectedSources: ConnectedSource[] = [
  { id: '1', name: 'SharePoint', connected: true, accessLevel: 'My access only' },
  { id: '2', name: 'OneDrive', connected: true, accessLevel: 'My access only' },
  { id: '3', name: 'Teams', connected: true, accessLevel: 'My access only' },
  { id: '4', name: 'Email/Calendar', connected: false, accessLevel: 'Not connected' },
]

const defaultBehaviors: AssistantBehavior[] = [
  { id: '1', label: 'Answers published info automatically' },
  { id: '2', label: 'Escalates low-confidence questions' },
  { id: '3', label: 'Requests approval for restricted topics' },
]

// Mock assistant connections - Who I can access
const mockCanAccess: AssistantConnection[] = [
  { id: '1', personName: 'Jack', personTitle: 'Senior Software Engineer', accessType: 'can-access' },
  { id: '2', personName: 'Sajid', personTitle: 'Software Engineer', accessType: 'can-access' },
  { id: '3', personName: 'Ahmed', personTitle: 'Product Manager', accessType: 'can-access' },
]

// Mock assistant connections - Who can access mine
const mockCanAccessMe: AssistantConnection[] = [
  { id: '4', personName: 'John Smith', personTitle: 'Engineering Manager', accessType: 'can-access-me', status: 'approved' },
  { id: '5', personName: 'Sarah Johnson', personTitle: 'Director of Engineering', accessType: 'can-access-me', status: 'approved' },
  { id: '6', personName: 'Michael Chen', personTitle: 'VP of Technology', accessType: 'can-access-me', status: 'approved' },
  { id: '7', personName: 'David Wilson', personTitle: 'Senior Product Manager', accessType: 'can-access-me', status: 'pending', requestedAt: new Date(Date.now() - 1000 * 60 * 60 * 24) },
  { id: '8', personName: 'Emily Brown', personTitle: 'Lead Designer', accessType: 'can-access-me', status: 'pending', requestedAt: new Date(Date.now() - 1000 * 60 * 60 * 12) },
]

// Mock organization hierarchy
const createMockOrgHierarchy = (currentUserName: string = 'Muhammad'): OrgNode => ({
  person: {
    id: 'ceo',
    name: 'Robert Anderson',
    title: 'Chief Executive Officer',
    department: 'Executive',
    assistantAccess: {
      public: true,
      restricted: false,
      private: false,
    },
  },
  children: [
    {
      person: {
        id: 'vp',
        name: 'Michael Chen',
        title: 'Vice President of Technology',
        department: 'Technology',
        reportsTo: 'Robert Anderson',
        assistantAccess: {
          public: true,
          restricted: false,
          private: false,
        },
      },
      children: [
        {
          person: {
            id: 'director',
            name: 'Sarah Johnson',
            title: 'Director of Engineering',
            department: 'Engineering',
            reportsTo: 'Michael Chen',
            assistantAccess: {
              public: true,
              restricted: true,
              private: false,
            },
          },
          children: [
            {
              person: {
                id: 'manager',
                name: 'John Smith',
                title: 'Engineering Manager',
                department: 'Engineering',
                reportsTo: 'Sarah Johnson',
                assistantAccess: {
                  public: true,
                  restricted: false,
                  private: false,
                },
              },
              children: [
                {
                  person: {
                    id: 'muhammad',
                    name: currentUserName,
                    title: 'Software Engineer',
                    department: 'Engineering',
                    reportsTo: 'John Smith',
                    assistantAccess: {
                      public: true,
                      restricted: false,
                      private: false,
                    },
                  },
                  children: [],
                },
                {
                  person: {
                    id: 'jack',
                    name: 'Jack',
                    title: 'Senior Software Engineer',
                    department: 'Engineering',
                    reportsTo: 'John Smith',
                    assistantAccess: {
                      public: true,
                      restricted: true,
                      private: false,
                    },
                  },
                  children: [],
                },
                {
                  person: {
                    id: 'sajid',
                    name: 'Sajid',
                    title: 'Software Engineer',
                    department: 'Engineering',
                    reportsTo: 'John Smith',
                    assistantAccess: {
                      public: false,
                      restricted: false,
                      private: true,
                    },
                  },
                  children: [],
                },
                {
                  person: {
                    id: 'ahmed',
                    name: 'Ahmed',
                    title: 'Product Manager',
                    department: 'Engineering',
                    reportsTo: 'John Smith',
                    assistantAccess: {
                      public: true,
                      restricted: false,
                      private: false,
                    },
                  },
                  children: [],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
})

const OrgTreeNode: React.FC<{
  node: OrgNode
  currentUserId: string
  onPersonClick: (person: Person) => void
  level?: number
}> = ({ node, currentUserId, onPersonClick, level = 0 }) => {
  const isCurrentUser = node.person.id === currentUserId.toLowerCase()
  const canClick = !isCurrentUser

  return (
    <div className="ml-4">
      <button
        onClick={() => canClick && onPersonClick(node.person)}
        disabled={!canClick}
        className={`
          w-full text-left p-2 rounded-md transition-colors
          ${canClick
            ? 'hover:bg-accent cursor-pointer'
            : 'opacity-60 cursor-default'
          }
        `}
      >
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{node.person.name}</span>
          {isCurrentUser && (
            <Badge variant="secondary" className="text-xs">You</Badge>
          )}
        </div>
        <div className="text-xs text-muted-foreground ml-6">
          {node.person.title} • {node.person.department}
        </div>
      </button>
      {node.children.length > 0 && (
        <div className="mt-1">
          {node.children.map((child) => (
            <OrgTreeNode
              key={child.person.id}
              node={child}
              currentUserId={currentUserId}
              onPersonClick={onPersonClick}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export const Profile: React.FC<ProfileProps> = ({
  isOpen,
  onClose,
  onTalkToAssistant,
  currentUserName = 'Muhammad',
}) => {
  const [activeTab, setActiveTab] = useState<'assistant' | 'organization'>('assistant')
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null)
  const [isPersonDetailOpen, setIsPersonDetailOpen] = useState(false)
  const [connectedSources, setConnectedSources] = useState<ConnectedSource[]>(mockConnectedSources)
  const [behaviors, setBehaviors] = useState<AssistantBehavior[]>(defaultBehaviors)
  const [newBehaviorText, setNewBehaviorText] = useState('')
  const [canAccessConnections] = useState<AssistantConnection[]>(mockCanAccess)
  const [canAccessMeConnections, setCanAccessMeConnections] = useState<AssistantConnection[]>(mockCanAccessMe)
  const [selectedConnection, setSelectedConnection] = useState<AssistantConnection | null>(null)
  const [isAccessDialogOpen, setIsAccessDialogOpen] = useState(false)
  const [guardrails, setGuardrails] = useState<Record<string, AccessGuardrails>>({})

  // Mock guardrails for never auto reply topics
  const neverAutoReplyOptions = [
    'Financial data and budget information',
    'HR and personnel matters',
    'Strategic planning and roadmaps',
    'Confidential project details',
    'Legal and compliance topics',
    'Performance reviews and evaluations',
  ]
  const { addToast } = useToast()
  const { conversations } = useConversations()
  const { reviews } = useReviews()

  // Calculate stats
  const totalInteractions = conversations.length
  const pendingApprovals = reviews.filter(r => r.status === 'pending').length

  // Generate graph data from connections
  const graphData = useMemo(() => {
    const nodes: GraphNode[] = []
    const edges: GraphEdge[] = []

    // Central node: My Assistant
    nodes.push({
      id: 'my-assistant',
      label: `${currentUserName}'s Assistant`,
      type: 'assistant',
      x: 300,
      y: 200,
      status: 'connected',
      description: 'Your personal digital assistant',
    })

    // Connected Sources
    connectedSources.forEach((source, index) => {
      const nodeId = `source-${source.id}`
      nodes.push({
        id: nodeId,
        label: source.name,
        type: 'source',
        x: 100 + (index * 150),
        y: 50,
        status: source.connected ? 'connected' : 'blocked',
        description: `Data source: ${source.accessLevel}`,
      })

      edges.push({
        id: `edge-source-${source.id}`,
        sourceId: 'my-assistant',
        targetId: nodeId,
        type: source.connected ? 'direct' : 'blocked',
      })
    })

    // People I can access (assistants only)
    canAccessConnections.forEach((conn, index) => {
      const nodeId = `can-access-${conn.id}`
      nodes.push({
        id: nodeId,
        label: `${conn.personName}'s Assistant`,
        type: 'agent',
        x: 500 + (index * 150),
        y: 100,
        status: 'connected',
        description: `Assistant for ${conn.personName} (${conn.personTitle})`,
      })

      edges.push({
        id: `edge-access-${conn.id}`,
        sourceId: 'my-assistant',
        targetId: nodeId,
        type: 'direct',
      })
    })

    return { nodes, edges }
  }, [connectedSources, canAccessConnections, currentUserName])

  const orgHierarchy = createMockOrgHierarchy(currentUserName)

  const handlePersonClick = (person: Person) => {
    setSelectedPerson(person)
    setIsPersonDetailOpen(true)
  }

  const handleToggleSource = (sourceId: string, checked: boolean) => {
    setConnectedSources(prev =>
      prev.map(source =>
        source.id === sourceId
          ? { ...source, connected: checked }
          : source
      )
    )
  }

  const handleAddBehavior = () => {
    if (newBehaviorText.trim()) {
      const newBehavior: AssistantBehavior = {
        id: `behavior-${Date.now()}`,
        label: newBehaviorText.trim(),
      }
      setBehaviors(prev => [...prev, newBehavior])
      setNewBehaviorText('')
    }
  }

  const handleRemoveBehavior = (behaviorId: string) => {
    setBehaviors(prev => prev.filter(b => b.id !== behaviorId))
  }

  // Helper function to find person in org hierarchy
  const findPersonInHierarchy = (node: OrgNode, personName: string): Person | null => {
    if (node.person.name === personName) {
      return node.person
    }
    for (const child of node.children) {
      const found = findPersonInHierarchy(child, personName)
      if (found) return found
    }
    return null
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className={cn(
          "max-h-[90vh] overflow-hidden flex flex-col max-w-6xl w-[95vw]"
        )}>
          <DialogHeader>
            <DialogTitle>Profile</DialogTitle>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="assistant">My Assistant</TabsTrigger>
              <TabsTrigger value="organization">Organization</TabsTrigger>
            </TabsList>

            <div className={cn(
              "flex-1 overflow-hidden mt-4",
              activeTab === 'assistant' && "overflow-y-auto"
            )}>
              <TabsContent value="assistant" className="space-y-6 mt-0">
                {/* Assistant Card */}
                <Card>
                  <CardHeader>
                    <CardTitle>{currentUserName}'s Assistant</CardTitle>
                    <CardDescription>Personal Digital Assistant</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <MessageCircle className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-2xl font-bold">{totalInteractions}</span>
                          <span className="text-sm text-muted-foreground">Total Interactions</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center">
                          <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-2xl font-bold text-orange-600 dark:text-orange-400">{pendingApprovals}</span>
                          <span className="text-sm text-muted-foreground">Pending Approvals</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Accordion type="multiple" defaultValue={['graph', 'connections', 'sources', 'behaviors']} className="w-full">
                  {/* Connections Graph */}
                  <AccordionItem value="graph">
                    <AccordionTrigger>Connections Graph</AccordionTrigger>
                    <AccordionContent>
                      <ConnectionsGraph
                        nodes={graphData.nodes}
                        edges={graphData.edges}
                      />
                    </AccordionContent>
                  </AccordionItem>

                  {/* Assistant Connections */}
                  <AccordionItem value="connections">
                    <AccordionTrigger>Assistant Connections</AccordionTrigger>
                    <AccordionContent>
                      <Tabs defaultValue="can-access" className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="can-access">I Can Consult</TabsTrigger>
                          <TabsTrigger value="can-access-me">Can Consult Mine</TabsTrigger>
                        </TabsList>

                        <TabsContent value="can-access" className="mt-4">
                          <div className="space-y-2">
                            {canAccessConnections.map((connection) => {
                              const person = findPersonInHierarchy(orgHierarchy, connection.personName)
                              return (
                                <div
                                  key={connection.id}
                                  className="flex items-center justify-between p-3 rounded-lg border bg-card"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                      <User className="h-4 w-4 text-primary" />
                                    </div>
                                    <div>
                                      <div className="font-medium text-sm">{connection.personName}'s Assistant</div>
                                      <div className="text-xs text-muted-foreground">{connection.personTitle}</div>
                                    </div>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      if (person) {
                                        onTalkToAssistant(person)
                                        onClose()
                                      }
                                    }}
                                  >
                                    <MessageSquare className="h-4 w-4 mr-2" />
                                    Message
                                  </Button>
                                </div>
                              )
                            })}
                            {canAccessConnections.length === 0 && (
                              <p className="text-sm text-muted-foreground py-2">No assistants accessible</p>
                            )}
                          </div>
                        </TabsContent>

                        <TabsContent value="can-access-me" className="mt-4">
                          <div className="space-y-2">
                            {/* Pending Requests */}
                            {canAccessMeConnections
                              .filter(conn => conn.status === 'pending')
                              .map((connection) => (
                                <div
                                  key={connection.id}
                                  className="flex items-center justify-between p-3 rounded-lg border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-950/20"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="h-8 w-8 rounded-full bg-yellow-100 dark:bg-yellow-900 flex items-center justify-center">
                                      <User className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                                    </div>
                                    <div>
                                      <div className="font-medium text-sm flex items-center gap-2">
                                        {connection.personName}
                                        <Badge variant="outline" className="bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700 text-xs">
                                          Pending
                                        </Badge>
                                      </div>
                                      <div className="text-xs text-muted-foreground">{connection.personTitle}</div>
                                      {connection.requestedAt && (
                                        <div className="text-xs text-muted-foreground mt-0.5">
                                          Requested {connection.requestedAt.toLocaleDateString()}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      variant="default"
                                      size="sm"
                                      className="bg-yellow-600 hover:bg-yellow-700 text-white"
                                      onClick={() => {
                                        setSelectedConnection(connection)
                                        setIsAccessDialogOpen(true)
                                        // Initialize guardrails for this connection if not exists
                                        if (!guardrails[connection.id]) {
                                          setGuardrails(prev => ({
                                            ...prev,
                                            [connection.id]: {
                                              accessEnabled: false,
                                              alwaysReview: true,
                                              confidenceThreshold: 80,
                                              neverAutoReplyTopics: [],
                                              allowedSources: {
                                                teams: true,
                                                sharepoint: true,
                                                onedrive: true,
                                                email: true,
                                              },
                                            },
                                          }))
                                        }
                                      }}
                                    >
                                      <CheckCircle2 className="h-4 w-4 mr-2" />
                                      Approve
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        setCanAccessMeConnections(prev => prev.filter(c => c.id !== connection.id))
                                        addToast({
                                          title: 'Request declined',
                                          description: `Access request from ${connection.personName} has been declined.`,
                                          variant: 'success',
                                        })
                                      }}
                                    >
                                      <X className="h-4 w-4 mr-2" />
                                      Decline
                                    </Button>
                                  </div>
                                </div>
                              ))}

                            {/* Approved Connections */}
                            {canAccessMeConnections
                              .filter(conn => conn.status === 'approved' || !conn.status)
                              .map((connection) => (
                                <div
                                  key={connection.id}
                                  className="flex items-center justify-between p-3 rounded-lg border bg-card"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                      <User className="h-4 w-4 text-primary" />
                                    </div>
                                    <div>
                                      <div className="font-medium text-sm">{connection.personName}</div>
                                      <div className="text-xs text-muted-foreground">{connection.personTitle}</div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedConnection(connection)
                                        setIsAccessDialogOpen(true)
                                        // Initialize guardrails for this connection if not exists
                                        if (!guardrails[connection.id]) {
                                          setGuardrails(prev => ({
                                            ...prev,
                                            [connection.id]: {
                                              accessEnabled: false,
                                              alwaysReview: true,
                                              confidenceThreshold: 80,
                                              neverAutoReplyTopics: [],
                                              allowedSources: {
                                                teams: true,
                                                sharepoint: true,
                                                onedrive: true,
                                                email: true,
                                              },
                                            },
                                          }))
                                        }
                                      }}
                                    >
                                      <Settings className="h-4 w-4 mr-2" />
                                      Manage Access
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        setCanAccessMeConnections(prev => prev.filter(c => c.id !== connection.id))
                                        addToast({
                                          title: 'Access revoked',
                                          description: `Access for ${connection.personName} has been revoked.`,
                                          variant: 'success',
                                        })
                                      }}
                                    >
                                      <ShieldOff className="h-4 w-4 mr-2" />
                                      Revoke
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            {canAccessMeConnections.length === 0 && (
                              <p className="text-sm text-muted-foreground py-2">No one has access to your assistant</p>
                            )}
                          </div>
                        </TabsContent>
                      </Tabs>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Connected Sources */}
                  <AccordionItem value="sources">
                    <AccordionTrigger>Connected Sources</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2">
                        {connectedSources.map((source) => (
                          <div
                            key={source.id}
                            className="flex items-center justify-between p-3 rounded-lg border bg-card"
                          >
                            <div className="flex items-center gap-3 flex-1">
                              {source.connected ? (
                                <CheckCircle2 className="h-5 w-5 text-green-600" />
                              ) : (
                                <XCircle className="h-5 w-5 text-muted-foreground" />
                              )}
                              <div className="flex-1">
                                <div className="font-medium text-sm">{source.name}</div>
                                <div className="text-xs text-muted-foreground">
                                  {source.connected ? (
                                    <>
                                      <span className="text-green-600">Connected</span> • {source.accessLevel}
                                    </>
                                  ) : (
                                    source.accessLevel
                                  )}
                                </div>
                              </div>
                            </div>
                            <Switch
                              checked={source.connected}
                              onCheckedChange={(checked) => handleToggleSource(source.id, checked)}
                            />
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Behavior Section */}
                  <AccordionItem value="behaviors">
                    <AccordionTrigger>Behavior Traits</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-4">
                        {/* Behavior Pills */}
                        <div className="flex flex-wrap gap-2">
                          {behaviors.map((behavior) => (
                            <Badge
                              key={behavior.id}
                              variant="secondary"
                              className="flex items-center gap-1.5 px-3 py-1.5 pr-1.5"
                            >
                              <span>{behavior.label}</span>
                              <button
                                onClick={() => handleRemoveBehavior(behavior.id)}
                                className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5 transition-colors"
                                aria-label={`Remove ${behavior.label}`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>

                        {/* Add New Behavior */}
                        <div className="flex gap-2">
                          <Input
                            placeholder="Add a new behavior trait..."
                            value={newBehaviorText}
                            onChange={(e) => setNewBehaviorText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleAddBehavior()
                              }
                            }}
                            className="flex-1"
                          />
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={handleAddBehavior}
                            disabled={!newBehaviorText.trim()}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </TabsContent>

              <TabsContent value="organization" className="mt-0 h-full flex flex-col">
                <div className="flex-1 border rounded-lg overflow-hidden min-h-[600px]">
                  <OrganizationView
                    rootNode={orgHierarchy}
                    currentUserId={currentUserName}
                    onPersonSelect={handlePersonClick}
                    onMessage={(person) => {
                      onTalkToAssistant(person)
                      onClose()
                    }}
                    currentUserAccessList={canAccessConnections}
                  />
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>

      <PersonDetailPanel
        person={selectedPerson}
        isOpen={isPersonDetailOpen}
        onClose={() => setIsPersonDetailOpen(false)}
        onTalkToAssistant={onTalkToAssistant}
        currentUserAccessList={canAccessConnections}
      />

      {/* Access Management Dialog */}
      <Dialog open={isAccessDialogOpen} onOpenChange={setIsAccessDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedConnection?.status === 'pending' ? 'Approve Access' : 'Manage Access'}
            </DialogTitle>
            <DialogDescription>
              {selectedConnection?.status === 'pending'
                ? `Configure access settings for ${selectedConnection?.personName}'s assistant`
                : `Manage access settings for ${selectedConnection?.personName}'s assistant`}
            </DialogDescription>
          </DialogHeader>

          {selectedConnection && (
            <div className="space-y-6 py-4">
              {/* Connection Info */}
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/50">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="font-medium">{selectedConnection.personName}</div>
                  <div className="text-sm text-muted-foreground">{selectedConnection.personTitle}</div>
                </div>
              </div>

              {/* Approve Access Toggle - Master Switch */}
              <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                <div className="space-y-0.5">
                  <Label htmlFor="approve-access" className="text-base font-semibold">
                    Approve Access
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Grant access to your assistant. If disabled, no access is granted.
                  </p>
                </div>
                <Switch
                  id="approve-access"
                  checked={guardrails[selectedConnection.id]?.accessEnabled ?? false}
                  onCheckedChange={(checked) => {
                    setGuardrails(prev => ({
                      ...prev,
                      [selectedConnection.id]: {
                        ...(prev[selectedConnection.id] || {
                          accessEnabled: checked,
                          alwaysReview: true,
                          confidenceThreshold: 80,
                          neverAutoReplyTopics: [],
                          allowedSources: { teams: true, sharepoint: true, onedrive: true, email: true },
                        }),
                        accessEnabled: checked,
                      },
                    }))
                  }}
                />
              </div>

              {/* All other settings only show if access is enabled */}
              {guardrails[selectedConnection.id]?.accessEnabled === true && (
                <>
                  {/* Guardrails Section */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Sliders className="h-5 w-5 text-primary" />
                      <h3 className="text-lg font-semibold">Guardrails & Configuration</h3>
                    </div>

                    {/* Always Review Toggle - Default: true */}
                    <div className="flex items-center justify-between p-4 rounded-lg border">
                      <div className="space-y-0.5">
                        <Label htmlFor="always-review" className="text-base font-medium">
                          Always Review
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Require manual review for all responses. If disabled, auto reply is enabled based on confidence threshold.
                        </p>
                      </div>
                      <Switch
                        id="always-review"
                        checked={guardrails[selectedConnection.id]?.alwaysReview ?? true}
                        onCheckedChange={(checked) => {
                          setGuardrails(prev => ({
                            ...prev,
                            [selectedConnection.id]: {
                              ...(prev[selectedConnection.id] || {
                                accessEnabled: false,
                                alwaysReview: true,
                                confidenceThreshold: 80,
                                neverAutoReplyTopics: [],
                                allowedSources: { teams: true, sharepoint: true, onedrive: true, email: true },
                              }),
                              alwaysReview: checked,
                            },
                          }))
                        }}
                      />
                    </div>

                    {/* Confidence Threshold Slider - Only show when Always Review is disabled */}
                    {!guardrails[selectedConnection.id]?.alwaysReview && (
                      <div className="p-4 rounded-lg border space-y-3">
                        <div className="space-y-1">
                          <Label className="text-base font-medium">
                            Confidence Threshold: {guardrails[selectedConnection.id]?.confidenceThreshold || 80}%
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Auto reply only when confidence is above this threshold. Below this, responses require review.
                          </p>
                        </div>
                        <Slider
                          value={[guardrails[selectedConnection.id]?.confidenceThreshold || 80]}
                          onValueChange={(value: number[]) => {
                            setGuardrails(prev => ({
                              ...prev,
                              [selectedConnection.id]: {
                                ...(prev[selectedConnection.id] || {
                                  accessEnabled: false,
                                  alwaysReview: false,
                                  confidenceThreshold: 80,
                                  neverAutoReplyTopics: [],
                                  allowedSources: { teams: true, sharepoint: true, onedrive: true, email: true },
                                }),
                                confidenceThreshold: value[0],
                              },
                            }))
                          }}
                          min={0}
                          max={100}
                          step={5}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Low (0%)</span>
                          <span>Medium (50%)</span>
                          <span>High (100%)</span>
                        </div>
                      </div>
                    )}

                    {/* Never Auto Reply Topics - Only show when Always Review is disabled */}
                    {!guardrails[selectedConnection.id]?.alwaysReview && (
                      <div className="p-4 rounded-lg border space-y-3">
                        <div className="space-y-1">
                          <Label className="text-base font-medium">Never Auto Reply Topics</Label>
                          <p className="text-sm text-muted-foreground">
                            Select topics that should always require review, regardless of confidence score
                          </p>
                        </div>
                        <div className="space-y-2">
                          {neverAutoReplyOptions.map((topic) => {
                            const isSelected = guardrails[selectedConnection.id]?.neverAutoReplyTopics?.includes(topic) || false
                            return (
                              <div
                                key={topic}
                                className={cn(
                                  "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                                  isSelected
                                    ? "bg-primary/10 border-primary"
                                    : "hover:bg-muted/50"
                                )}
                                onClick={() => {
                                  const currentTopics = guardrails[selectedConnection.id]?.neverAutoReplyTopics || []
                                  setGuardrails(prev => ({
                                    ...prev,
                                    [selectedConnection.id]: {
                                      ...(prev[selectedConnection.id] || {
                                        accessEnabled: false,
                                        alwaysReview: false,
                                        confidenceThreshold: 80,
                                        neverAutoReplyTopics: [],
                                        allowedSources: { teams: true, sharepoint: true, onedrive: true, email: true },
                                      }),
                                      neverAutoReplyTopics: isSelected
                                        ? currentTopics.filter(t => t !== topic)
                                        : [...currentTopics, topic],
                                    },
                                  }))
                                }}
                              >
                                <div className={cn(
                                  "h-4 w-4 rounded border-2 flex items-center justify-center",
                                  isSelected ? "bg-primary border-primary" : "border-muted-foreground"
                                )}>
                                  {isSelected && <CheckCircle2 className="h-3 w-3 text-primary-foreground" />}
                                </div>
                                <span className="text-sm">{topic}</span>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Sources Section */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-primary" />
                      <h3 className="text-lg font-semibold">Allowed Sources</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { key: 'teams', label: 'Teams', icon: MessageSquare },
                        { key: 'sharepoint', label: 'SharePoint', icon: MessageSquare },
                        { key: 'onedrive', label: 'OneDrive', icon: MessageSquare },
                        { key: 'email', label: 'Email', icon: MessageSquare },
                      ].map((source) => {
                        const Icon = source.icon
                        const isEnabled = guardrails[selectedConnection.id]?.allowedSources?.[source.key as keyof typeof guardrails[string]['allowedSources']] ?? true
                        return (
                          <div
                            key={source.key}
                            className={cn(
                              "flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors",
                              isEnabled ? "bg-primary/10 border-primary" : "hover:bg-muted/50"
                            )}
                            onClick={() => {
                              setGuardrails(prev => ({
                                ...prev,
                                [selectedConnection.id]: {
                                  ...(prev[selectedConnection.id] || {
                                    accessEnabled: false,
                                    alwaysReview: true,
                                    confidenceThreshold: 80,
                                    neverAutoReplyTopics: [],
                                    allowedSources: { teams: true, sharepoint: true, onedrive: true, email: true },
                                  }),
                                  allowedSources: {
                                    ...(prev[selectedConnection.id]?.allowedSources || { teams: true, sharepoint: true, onedrive: true, email: true }),
                                    [source.key]: !isEnabled,
                                  },
                                },
                              }))
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              <span className="text-sm font-medium">{source.label}</span>
                            </div>
                            <Switch
                              checked={isEnabled}
                              onCheckedChange={() => {
                                setGuardrails(prev => ({
                                  ...prev,
                                  [selectedConnection.id]: {
                                    ...(prev[selectedConnection.id] || {
                                      accessEnabled: false,
                                      alwaysReview: true,
                                      confidenceThreshold: 80,
                                      neverAutoReplyTopics: [],
                                      allowedSources: { teams: true, sharepoint: true, onedrive: true, email: true },
                                    }),
                                    allowedSources: {
                                      ...(prev[selectedConnection.id]?.allowedSources || { teams: true, sharepoint: true, onedrive: true, email: true }),
                                      [source.key]: !isEnabled,
                                    },
                                  },
                                }))
                              }}
                            />
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsAccessDialogOpen(false)
                    setSelectedConnection(null)
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    const accessEnabled = guardrails[selectedConnection.id]?.accessEnabled ?? false

                    if (selectedConnection.status === 'pending') {
                      if (accessEnabled) {
                        setCanAccessMeConnections(prev =>
                          prev.map(c =>
                            c.id === selectedConnection.id
                              ? { ...c, status: 'approved' as const }
                              : c
                          )
                        )
                        addToast({
                          title: 'Access approved',
                          description: `Access for ${selectedConnection.personName} has been approved with configured settings.`,
                          variant: 'success',
                        })
                      } else {
                        // If access is disabled, remove the connection
                        setCanAccessMeConnections(prev => prev.filter(c => c.id !== selectedConnection.id))
                        addToast({
                          title: 'Request declined',
                          description: `Access request from ${selectedConnection.personName} has been declined.`,
                          variant: 'success',
                        })
                      }
                    } else {
                      if (!accessEnabled) {
                        // If access is disabled, remove the connection
                        setCanAccessMeConnections(prev => prev.filter(c => c.id !== selectedConnection.id))
                        addToast({
                          title: 'Access revoked',
                          description: `Access for ${selectedConnection.personName} has been revoked.`,
                          variant: 'success',
                        })
                      } else {
                        addToast({
                          title: 'Settings saved',
                          description: `Access settings for ${selectedConnection.personName} have been updated.`,
                          variant: 'success',
                        })
                      }
                    }
                    setIsAccessDialogOpen(false)
                    setSelectedConnection(null)
                  }}
                >
                  {selectedConnection.status === 'pending'
                    ? (guardrails[selectedConnection.id]?.accessEnabled === true ? 'Approve & Save' : 'Decline')
                    : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

